/**
 * Reiter „Daten" (Bildschirm 2): die Datenlandschaft über alle Kunden und
 * Systeme. Fünf Puls-Kacheln, Publivo-Gesundheit, Tagesrhythmus, Kanäle und
 * vier Karten mit dem, was die Daten sonst noch sagen.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDaten, zahl, type Reihe } from '@/lib/leitwarte-api';
import { Karte, Kopf, Legende, Laden, Leer, Fehlerzeile, Swatch, T, kapital, summe, prozent, parseDatum, monatName, wochentagKurz, mehrzahl } from './bausteine';
import { Sparkline, Saeulen, Heatmap, Balkenzeile, Segmente, RAMPE } from './charts';

// ── Übersetzungen ────────────────────────────────────────────────────────────

const KANAL: Record<string, string> = { paid: 'Ads', ads: 'Ads', cpc: 'Ads', direct: 'Direkt', direkt: 'Direkt', organic: 'Google', organisch: 'Google', referral: 'Verweis', social: 'Social', ai: 'KI', ki: 'KI', unbekannt: 'ohne', unknown: 'ohne', ohne: 'ohne', '': 'ohne' };
const istOhne = (l: string) => ['unbekannt', 'unknown', 'ohne', ''].includes(l.toLowerCase());
const kanalName = (l: string) => KANAL[l.toLowerCase()] ?? kapital(l);

const GERAET: Record<string, string> = { mobile: 'Handy', smartphone: 'Handy', phone: 'Handy', handy: 'Handy', desktop: 'Desktop', tablet: 'Tablet' };
const geraetName = (l: string) => GERAET[l.toLowerCase()] ?? kapital(l);

function formularGruppe(label: string): string {
  const l = label.toLowerCase();
  if (l.startsWith('bewerbung')) return 'Bewerbungen';
  if (l.startsWith('kontakt')) return 'Kontakt';
  if (l.startsWith('potenzial')) return 'Potenzial-Check';
  if (l.startsWith('angebot')) return 'Angebot';
  if (l.startsWith('rueckruf') || l.startsWith('rückruf') || l.startsWith('callback')) return 'Rückruf';
  return kapital(label.replace(/[_-]+/g, ' '));
}

function templateName(t: string): string {
  const l = t.toLowerCase();
  if (l.includes('announce') || l.includes('ankuend') || l.includes('ankünd')) return 'Ankündigungen';
  if (l.includes('digest')) return 'Wochen-Digest';
  if (l.includes('trial')) return 'Trial-Erinnerung';
  if (l.includes('welcome') || l.includes('onboard')) return 'Willkommen';
  return kapital(t.replace(/[_-]+/g, ' '));
}

/** Health-Score-Klassen der Mockups; null = kein Wert. */
function healthFarbe(v: number | null): string {
  if (v == null) return RAMPE[0];
  if (v < 30) return '#0d366b';
  if (v < 50) return '#104281';
  if (v < 65) return '#184f95';
  if (v < 80) return '#256abf';
  if (v < 90) return '#3987e5';
  return '#6da7ec';
}

/** '2026-07-13' → '13.7.' beim ersten Eintrag und bei Monatswechsel, sonst '13.'. */
function wochenKopf(wochen: string[]): string[] {
  let monat = -1;
  return wochen.map((w) => {
    const d = parseDatum(w);
    if (!d) return w;
    const m = d.getMonth();
    const s = m !== monat ? `${d.getDate()}.${m + 1}.` : `${d.getDate()}.`;
    monat = m;
    return s;
  });
}

const monatIm = (w: string) => {
  const d = parseDatum(w);
  return d ? `im ${monatName(w)}` : 'zu Beginn';
};

/** Summe aller Reihen, deren Label einen der Namen enthält. */
const reihenWert = (reihen: Reihe[] | undefined, ...namen: string[]) =>
  (reihen ?? []).filter((r) => namen.some((n) => r.label.toLowerCase().includes(n))).reduce((a, r) => a + r.wert, 0);

const WT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const STUNDEN_ACHSE = [0, 6, 9, 12, 18, 23].map((i) => ({ i, text: String(i) }));
const spitze = (xs: number[]) => (xs.length ? xs.indexOf(Math.max(...xs)) : -1);

// ── Bausteine ────────────────────────────────────────────────────────────────

function Puls({ label, wert, rechts, fuss }: { label: string; wert: ReactNode; rechts?: ReactNode; fuss?: ReactNode }) {
  return (
    <Karte eng>
      <span className="lw-12 lw-sek">{label}</span>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8, minHeight: 40 }}>
        <span style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{wert}</span>
        {rechts}
      </div>
      {fuss ? <span className="lw-11 lw-ged">{fuss}</span> : null}
    </Karte>
  );
}

function NichtAngebunden({ label, was, eng = true }: { label: string; was: string; eng?: boolean }) {
  return (
    <Karte eng={eng}>
      {eng ? <span className="lw-12 lw-sek">{label}</span> : <Kopf titel={label} />}
      <span style={{ fontSize: 18, fontWeight: 700, color: T.ged, lineHeight: 1.2, minHeight: 40, display: 'flex', alignItems: 'flex-end' }}>Nicht angebunden</span>
      <span className="lw-11 lw-ged">{was}</span>
    </Karte>
  );
}

// ── Seite ────────────────────────────────────────────────────────────────────

export default function Daten() {
  const q = useQuery({ queryKey: ['leitwarte', 'daten'], queryFn: getDaten, staleTime: 10 * 60 * 1000 });
  if (!q.data) return q.isLoading ? <Laden text="Lade die Datenlandschaft …" /> : <Fehlerzeile error={q.error} retry={() => q.refetch()} />;

  const d = q.data;
  const tage = d.zeitraum_tage || 90;
  const besucher = d.besucher;
  const google = d.google;
  const anfragen = d.anfragen;
  const publivo = d.publivo;
  const blog = d.blog;
  const potenzial = d.potenzial;

  // Puls 1: Besucher
  const bw = besucher?.wochen ?? [];
  const bwErste = bw[0];
  const bwLetzte = bw[bw.length - 1];

  // Puls 2: Google
  const gw = google?.wochen ?? [];
  const gwLetzte = gw[gw.length - 1];

  // Puls 3: Publivo
  const jeMonat = (publivo?.je_monat ?? []).slice(-6);
  const jeMonatLetzter = jeMonat[jeMonat.length - 1];
  const statusWert = (n: string) => reihenWert(publivo?.status, n);
  const publivoFuss = [
    jeMonatLetzter ? `${zahl(jeMonatLetzter.wert)} ${monatIm(jeMonatLetzter.label)}` : null,
    `${zahl(statusWert('review'))} warten auf Freigabe`,
    `${zahl(statusWert('scheduled'))} geplant`,
    statusWert('failed') > 0 ? `${zahl(statusWert('failed'))} fehlgeschlagen` : null,
  ].filter(Boolean).join(', ');

  // Puls 5: Reaktionen
  const zernio = publivo?.zernio30 ?? [];
  const nachrichten = reihenWert(zernio, 'message.received');
  const kommentare = reihenWert(zernio, 'comment.received');
  const live = reihenWert(zernio, 'published');
  const fehlgeschlagen = reihenWert(zernio, 'failed');
  const bewertungen = reihenWert(zernio, 'review');

  // Publivo-Gesundheit
  const health = publivo?.health ?? { wochen: [], orgs: [] };
  const istTrial = (s: string) => /trial|test/i.test(s ?? '');
  const nachScore = <O extends { aktuell: number | null }>(xs: O[]) => [...xs].sort((a, b) => (b.aktuell ?? -1) - (a.aktuell ?? -1));
  const zahlende = nachScore(health.orgs.filter((o) => !istTrial(o.status)));
  const trials = nachScore(health.orgs.filter((o) => istTrial(o.status)));
  const healthZeilen = [
    ...zahlende.map((o) => ({ label: o.name, werte: o.werte, rechts: o.aktuell != null ? zahl(Math.round(o.aktuell)) : '' })),
    ...trials.map((o) => ({ label: o.name, werte: o.werte, rechts: o.aktuell != null ? zahl(Math.round(o.aktuell)) : '', gruppe: 'Trials' })),
  ];
  const healthOrgs = [...zahlende, ...trials];
  // Wer hat sich am stärksten bewegt? Erster und letzter Wert je Kunde.
  const bewegungen = healthOrgs
    .map((o) => {
      const w = o.werte.filter((v): v is number => v != null);
      return w.length >= 2 ? { name: o.name, von: Math.round(w[0]), bis: Math.round(w[w.length - 1]), delta: w[w.length - 1] - w[0] } : null;
    })
    .filter((b): b is { name: string; von: number; bis: number; delta: number } => b != null);
  const aufsteiger = [...bewegungen].sort((a, b) => b.delta - a.delta)[0];
  const absteiger = [...bewegungen].sort((a, b) => a.delta - b.delta)[0];
  const healthHinweis = [
    aufsteiger && aufsteiger.delta >= 20 ? `${aufsteiger.name} von ${aufsteiger.von} auf ${aufsteiger.bis}.` : null,
    absteiger && absteiger.delta <= -20 ? `${absteiger.name} im Sinkflug, von ${absteiger.von} auf ${absteiger.bis}.` : null,
  ].filter(Boolean).join(' ');

  // Tagesrhythmus
  const bs = besucher?.stunden ?? [];
  const ps = publivo?.stunden ?? [];
  const bsMax = Math.max(0, ...bs);
  let bisStunde = -1;
  bs.forEach((v, i) => { if (bsMax > 0 && v >= bsMax * 0.35) bisStunde = i; });
  const postStunde = spitze(ps);
  const rhythmusHinweis = bs.length && ps.length && postStunde >= 0
    ? `Gepostet wird um ${postStunde}, gelesen wird bis ${bisStunde}.${bisStunde - postStunde >= 6 ? ' Der Abend ist unbespielt.' : ''}`
    : bs.length ? `Die stärkste Stunde ist ${spitze(bs)} Uhr.` : null;

  const kanaele = (reihen: Reihe[] | undefined) => {
    const sortiert = [...(reihen ?? [])].sort((a, b) => (istOhne(a.label) ? 1 : istOhne(b.label) ? -1 : b.wert - a.wert));
    return sortiert.map((r) => ({ label: kanalName(r.label), wert: r.wert, farbe: istOhne(r.label) ? T.rand2 : undefined, wertFarbe: istOhne(r.label) ? T.sek : undefined }));
  };

  // Wer die Blogs liest
  const istMensch = (l: string) => /mensch|human|people|besucher/i.test(l);
  const leser = [...(blog?.leser ?? [])].sort((a, b) => (istMensch(a.label) ? -1 : istMensch(b.label) ? 1 : b.wert - a.wert)).slice(0, 10);
  const leserGesamt = summe((blog?.leser ?? []).map((r) => r.wert));
  const maschinen = summe((blog?.leser ?? []).filter((r) => !istMensch(r.label)).map((r) => r.wert));
  const maschinenPct = prozent(maschinen, leserGesamt);

  // Wonach gesucht wird
  const queries = [...(google?.top_queries ?? [])].sort((a, b) => b.klicks - a.klicks).slice(0, 6);
  const maxKlicks = Math.max(1, ...queries.map((x) => x.klicks));
  const adsTerms = [...(google?.ads_terms ?? [])].sort((a, b) => b.conversions - a.conversions || b.klicks - a.klicks).slice(0, 4);

  // Was aus Anfragen wird
  const ausgang = anfragen?.ausgang ?? [];
  const ausgangWert = (re: RegExp) => ausgang.filter((r) => re.test(r.label.toLowerCase())).reduce((a, r) => a + r.wert, 0);
  const gewonnen = ausgangWert(/gewonn|won/);
  const verloren = ausgangWert(/verlor|lost/);
  const spam = ausgangWert(/spam/);
  const unbekannt = ausgangWert(/unbek|unknown|offen/);
  const ausgangGesamt = gewonnen + verloren + spam + unbekannt || anfragen?.gesamt180 || 0;

  const gruppen = new Map<string, number>();
  for (const r of anfragen?.formulare90 ?? []) gruppen.set(formularGruppe(r.label), (gruppen.get(formularGruppe(r.label)) ?? 0) + r.wert);
  const formulareSortiert = [...gruppen.entries()].sort((a, b) => b[1] - a[1]);
  const formulareTop = formulareSortiert.slice(0, 5).map(([label, wert]) => ({ label, wert }));
  const formulareRest = formulareSortiert.slice(5);
  if (formulareRest.length) {
    const namen = formulareRest.map(([l]) => l);
    formulareTop.push({ label: namen.length <= 2 ? namen.join(', ') : `${namen.slice(0, 2).join(', ')}, Rest`, wert: summe(formulareRest.map(([, w]) => w)) });
  }

  // Kleinkram
  const mails = [...(publivo?.mails ?? [])]
    .map((m) => ({ label: templateName(m.template), wert: m.gesendet > 0 ? prozent(m.geoeffnet, m.gesendet) : 0, titel: `${zahl(m.geoeffnet)} von ${zahl(m.gesendet)} geöffnet` }))
    .sort((a, b) => b.wert - a.wert)
    .slice(0, 4)
    .map((m) => ({ ...m, anzeige: `${m.wert} %` }));
  const geraete = [...(besucher?.geraete ?? [])].sort((a, b) => b.wert - a.wert).slice(0, 3);
  const geraeteGesamt = summe(geraete.map((g) => g.wert));
  const geraeteFarben = [T.blau, RAMPE[5], RAMPE[3]];
  const wochentage = (publivo?.wochentage ?? []).map((w, i) => ({ tag: WT[i] ?? wochentagKurz(i + 1), wert: w })).sort((a, b) => b.wert - a.wert);
  const gewerke = (potenzial?.gewerke ?? []).slice(0, 4);
  const luecken = d.luecken ?? [];

  return (
    <>
      <div className="lw-kopf" style={{ gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Datenlandschaft</span>
        <span className="lw-sek">alle Kunden, alle Systeme, letzte {zahl(tage)} Tage</span>
      </div>

      {/* Puls */}
      <div className="lw-grid lw-g-puls">
        <Puls
          label="Besucher auf allen Kundenseiten, 30 Tage"
          wert={zahl(besucher?.unique30 ?? 0)}
          rechts={bw.length > 1 ? <Sparkline werte={bw.map((w) => w.wert)} breite={120} hoehe={40} punkte="keine" label="Seitenaufrufe je Woche" /> : null}
          fuss={bw.length >= 2 ? `Aufrufe je Woche: ${zahl(bwErste.wert)} ${monatIm(bwErste.woche)}, ${zahl(bwLetzte.wert)} letzte Woche` : 'Noch keine Wochenwerte'}
        />
        <Puls
          label="Google-Klicks je Woche, alle Properties"
          wert={zahl(gwLetzte?.wert ?? 0)}
          rechts={gw.length > 1 ? <Sparkline werte={gw.map((w) => w.wert)} breite={120} hoehe={40} punkte="keine" label="Klicks je Woche" /> : null}
          fuss={google?.hinweis
            ? <><span style={{ color: T.ernst, fontWeight: 800 }}>!</span> {google.hinweis}</>
            : gwLetzte?.wert2 != null ? `${zahl(gwLetzte.wert2)} Mal gesehen in der letzten Woche` : 'Noch keine Search-Console-Daten'}
        />
        {publivo?.verfuegbar ? (
          <Puls
            label="Beiträge über Publivo veröffentlicht"
            wert={zahl(publivo.veroeffentlicht_gesamt)}
            rechts={jeMonat.length ? <Saeulen werte={jeMonat.map((r) => r.wert)} breite={120} hoehe={40} luecke={6} radius={2} titel={(i, v) => `${monatName(jeMonat[i].label)}: ${zahl(v)}`} label="Veröffentlichungen je Monat" /> : null}
            fuss={publivoFuss}
          />
        ) : (
          <NichtAngebunden label="Beiträge über Publivo veröffentlicht" was="Publivo liefert gerade keine Daten." />
        )}
        {blog?.verfuegbar ? (
          <Puls
            label={`Blog-Aufrufe, ${zahl(tage)} Tage`}
            wert={zahl(blog.aufrufe90)}
            rechts={(blog.wochen ?? []).length > 1 ? <Sparkline werte={blog.wochen.map((w) => w.wert)} breite={120} hoehe={40} punkte="keine" label="Blog-Aufrufe je Woche" /> : null}
            fuss={`dazu ${zahl(blog.ki_treffer90)} Treffer von KI-Suchmaschinen`}
          />
        ) : (
          <NichtAngebunden label={`Blog-Aufrufe, ${zahl(tage)} Tage`} was="Die Blog-Statistik ist nicht angebunden." />
        )}
        {publivo?.verfuegbar ? (
          <Puls
            label="Reaktionen auf Kundenkanälen, 30 Tage"
            wert={zahl(nachrichten + kommentare)}
            rechts={<span className="lw-12 lw-sek" style={{ paddingBottom: 3, textAlign: 'right', lineHeight: 1.3 }}>{zahl(nachrichten)} Nachrichten<br />{zahl(kommentare)} Kommentare</span>}
            fuss={`${zahl(live)} Beiträge live gegangen, ${zahl(fehlgeschlagen)} fehlgeschlagen, ${zahl(bewertungen)} neue ${mehrzahl(bewertungen, 'Bewertung', 'Bewertungen')}`}
          />
        ) : (
          <NichtAngebunden label="Reaktionen auf Kundenkanälen, 30 Tage" was="Kommt mit der Publivo-Anbindung." />
        )}
      </div>

      {/* Publivo-Gesundheit und Tagesrhythmus */}
      <div className="lw-grid lw-g-mitte">
        {publivo?.verfuegbar ? (
          <Karte style={{ gap: 10 }}>
            <Kopf titel={`Publivo-Gesundheit je Kunde, ${zahl(health.wochen.length)} Wochen`} sub="Wochenmittel des Health-Scores, hell = gesund" />
            {healthZeilen.length ? (
              <div className="lw-scroll-x">
                <Heatmap
                  zeilen={healthZeilen}
                  spalten={wochenKopf(health.wochen)}
                  farbe={healthFarbe}
                  zellHoehe={22}
                  labelBreite={150}
                  spaltenBreite="minmax(0, 30px)"
                  rechtsBreite={44}
                  rechtsKopf="jetzt"
                  kopfGroesse={11}
                  titel={(r, c, v) => `${healthOrgs[r]?.name ?? ''}, Woche ${health.wochen[c] ?? ''}: ${v == null ? 'kein Wert' : zahl(Math.round(v))}`}
                />
              </div>
            ) : (
              <Leer text="Noch keine Health-Scores." />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: T.ged, flexWrap: 'wrap', marginTop: 'auto' }}>
              <span>unter 30</span>
              {['#0d366b', '#104281', '#184f95', '#256abf', '#3987e5', '#6da7ec'].map((f) => <Swatch key={f} farbe={f} />)}
              <span>90 und mehr</span>
              <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 8 }}><Swatch farbe={RAMPE[0]} />kein Wert</span>
              {healthHinweis ? <span style={{ marginLeft: 'auto', color: T.sek }}>{healthHinweis}</span> : null}
            </div>
          </Karte>
        ) : (
          <NichtAngebunden eng={false} label="Publivo-Gesundheit je Kunde" was="Publivo liefert gerade keine Daten." />
        )}

        <Karte style={{ gap: 12 }}>
          <Kopf titel="Tagesrhythmus" sub="wann Besucher kommen, wann Publivo postet" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lw-12 lw-sek">Seitenaufrufe je Stunde, {zahl(tage)} Tage, alle Kundenseiten</span>
            {bs.length ? <Saeulen werte={bs} voll achse={STUNDEN_ACHSE} titel={(i, v) => `${i} Uhr: ${zahl(v)} Aufrufe`} label="Seitenaufrufe je Stunde" /> : <Leer text="Keine Stundenwerte." />}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lw-12 lw-sek">Publivo-Veröffentlichungen je Stunde, alle Kunden</span>
            {publivo?.verfuegbar && ps.length ? (
              <Saeulen werte={ps} voll farbe={T.orange} achse={STUNDEN_ACHSE} grundlinie titel={(i, v) => `${i} Uhr: ${zahl(v)} Veröffentlichungen`} label="Veröffentlichungen je Stunde" />
            ) : (
              <Leer text={publivo?.verfuegbar ? 'Keine Stundenwerte.' : 'Nicht angebunden.'} />
            )}
          </div>
          <Legende eintraege={[{ farbe: T.blau, label: 'Besucher' }, { farbe: T.orange, label: 'Veröffentlichungen' }]} rechts={rhythmusHinweis} />
          <div className="lw-grid lw-g-zwei" style={{ marginTop: 'auto', fontSize: 12 }}>
            <div className="lw-innen" style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lw-ged">Besucher nach Kanal, {zahl(tage)} Tage</span>
              {besucher?.kanaele?.length ? <Balkenzeile zeilen={kanaele(besucher.kanaele)} labelBreite={62} wertBreite={44} luecke={6} abstand={4} /> : <Leer text="Keine Kanaldaten." />}
            </div>
            <div className="lw-innen" style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lw-ged">Anfragen nach Kanal, 90 Tage</span>
              {anfragen?.kanaele90?.length ? <Balkenzeile zeilen={kanaele(anfragen.kanaele90)} labelBreite={62} wertBreite={44} luecke={6} abstand={4} /> : <Leer text="Keine Kanaldaten." />}
            </div>
          </div>
        </Karte>
      </div>

      {/* Vier Karten unten */}
      <div className="lw-grid lw-g-vier">
        {blog?.verfuegbar ? (
          <Karte style={{ padding: '16px 18px' }}>
            <Kopf titel="Wer die Blogs liest" />
            <span className="lw-sub">Zugriffe auf alle Publivo-Blogs, {zahl(tage)} Tage</span>
            {leser.length ? (
              <Balkenzeile zeilen={leser.map((r) => ({ label: r.label, wert: r.wert, farbe: istMensch(r.label) ? T.blau : T.aqua }))} labelBreite={96} wertBreite={40} luecke={8} />
            ) : (
              <Leer text="Noch keine Zugriffe." />
            )}
            <Legende groesse={11} eintraege={[{ farbe: T.blau, label: 'Menschen' }, { farbe: T.aqua, label: 'Maschinen' }]} />
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {leserGesamt > 0 ? (
                <span className="lw-12 lw-sek">
                  {maschinenPct} % der Blog-Zugriffe sind Maschinen.{maschinenPct >= 50 ? ' Das ist dein KI-Sichtbarkeits-Argument, mit Zahlen.' : ''}
                </span>
              ) : null}
              {blog.lesezeit_sek != null || blog.scroll_pct != null ? (
                <span className="lw-11 lw-ged">
                  {blog.lesezeit_sek != null ? `Lesezeit im Schnitt ${Math.floor(blog.lesezeit_sek / 60)}:${String(Math.round(blog.lesezeit_sek % 60)).padStart(2, '0')} min` : null}
                  {blog.lesezeit_sek != null && blog.scroll_pct != null ? ' · ' : null}
                  {blog.scroll_pct != null ? `Scrolltiefe ${Math.round(blog.scroll_pct)} %` : null}
                </span>
              ) : null}
            </div>
          </Karte>
        ) : (
          <NichtAngebunden eng={false} label="Wer die Blogs liest" was="Die Blog-Statistik ist nicht angebunden." />
        )}

        <Karte style={{ padding: '16px 18px' }}>
          <Kopf titel="Wonach gesucht wird" />
          <span className="lw-sub">Google-Suchen mit Klick, 30 Tage, alle Kunden</span>
          {queries.length ? (
            <Balkenzeile
              anordnung="unter"
              max={maxKlicks}
              hoehe={8}
              wertBreite={34}
              zeilen={queries.map((x) => ({
                label: (
                  <>
                    {x.query}
                    {x.impressionen >= 50 && x.klicks * 10 <= x.impressionen ? <span className="lw-ged"> · {zahl(x.impressionen)} mal gesehen</span> : null}
                  </>
                ),
                wert: x.klicks,
                titel: `${x.query}: ${zahl(x.klicks)} Klicks, ${zahl(x.impressionen)} mal gesehen`,
              }))}
            />
          ) : (
            <Leer text="Keine Suchanfragen mit Klick." />
          )}
          {adsTerms.length ? (
            <>
              <span className="lw-sub" style={{ marginTop: 6 }}>Google Ads: Suchbegriffe mit den meisten Anfragen</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: T.sek }}>
                {adsTerms.map((t) => (
                  <div key={t.term} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.term}</span>
                    <span className="lw-weiss lw-fett" style={{ whiteSpace: 'nowrap' }}>
                      {t.conversions > 0 ? `${zahl(t.klicks)} · ${zahl(t.conversions)} ${mehrzahl(t.conversions, 'Anfrage', 'Anfragen')}` : `${zahl(t.klicks)} Klicks`}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </Karte>

        <Karte style={{ padding: '16px 18px' }}>
          <Kopf titel="Was aus Anfragen wird" />
          <span className="lw-sub">{zahl(anfragen?.gesamt180 ?? 0)} Anfragen in 180 Tagen</span>
          <Segmente
            teile={[
              { wert: gewonnen, farbe: T.blau, label: 'gewonnen' },
              { wert: verloren, farbe: T.orange, label: 'verloren' },
              { wert: spam, farbe: T.rand2, label: 'Spam' },
              { wert: unbekannt, farbe: T.innen, label: 'Ausgang unbekannt' },
            ]}
            label="Ausgang der Anfragen"
          />
          <Legende
            eintraege={[
              { farbe: T.blau, label: `gewonnen ${zahl(gewonnen)}` },
              { farbe: T.orange, label: `verloren ${zahl(verloren)}` },
              { farbe: T.rand2, label: `Spam ${zahl(spam)}` },
              { farbe: T.innen, rand: T.rand2, label: `Ausgang unbekannt ${zahl(unbekannt)}` },
            ]}
          />
          <span className="lw-sub" style={{ marginTop: 6 }}>Wofür angefragt wird, 90 Tage</span>
          {formulareTop.length ? <Balkenzeile zeilen={formulareTop} labelBreite={110} wertBreite={30} luecke={8} /> : <Leer text="Keine Formulare im Zeitraum." />}
          {ausgangGesamt > 0 && unbekannt / ausgangGesamt >= 0.5 ? (
            <span className="lw-12 lw-sek" style={{ marginTop: 'auto' }}>
              <span style={{ color: T.ernst, fontWeight: 800 }}>!</span> Bei {zahl(unbekannt)} von {zahl(ausgangGesamt)} Anfragen weiß das System nicht, was daraus wurde.
            </span>
          ) : null}
        </Karte>

        <Karte style={{ padding: '16px 18px', gap: 10 }}>
          <Kopf titel="Kleinkram, der trotzdem etwas sagt" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
            <div className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lw-ged">Publivo-Mails an Kunden, geöffnet</span>
              {mails.length ? <Balkenzeile zeilen={mails} max={Math.max(1, ...mails.map((m) => m.wert))} labelBreite={110} wertBreite={40} hoehe={8} luecke={8} abstand={4} /> : <span className="lw-ged">{publivo?.verfuegbar ? 'Keine Mails im Zeitraum.' : 'Nicht angebunden.'}</span>}
            </div>
            <div className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lw-ged">Potenzial-Check auf nico-els.de</span>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: T.sek }}>
                <span>{zahl(potenzial?.checks ?? 0)} Checks, {zahl(potenzial?.mit_email ?? 0)} mit E-Mail</span>
                <span className="lw-weiss lw-fett">{prozent(potenzial?.mit_email ?? 0, potenzial?.checks ?? 0)} %</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: T.sek }}>
                <span>{gewerke.length ? gewerke.map((g) => `${kapital(g.label)} ${zahl(g.wert)}`).join(' · ') : 'Noch keine Gewerke'}</span>
                <span className="lw-weiss lw-fett" style={{ whiteSpace: 'nowrap' }}>{zahl(potenzial?.berichte ?? 0)} {mehrzahl(potenzial?.berichte ?? 0, 'Bericht', 'Berichte')}</span>
              </div>
            </div>
            <div className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lw-ged">Geräte der Besucher</span>
              <Segmente hoehe={10} radius={4} teile={geraete.map((g, i) => ({ wert: g.wert, farbe: geraeteFarben[i] ?? RAMPE[2], label: geraetName(g.label) }))} label="Geräte der Besucher" />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: T.sek, flexWrap: 'wrap' }}>
                {geraete.map((g) => <span key={g.label}>{geraetName(g.label)} {prozent(g.wert, geraeteGesamt)} %</span>)}
                {geraete.length === 0 ? <span className="lw-ged">Keine Gerätedaten.</span> : null}
              </div>
            </div>
            <div className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lw-ged">Publivo-Wochentage der Veröffentlichung</span>
              {wochentage.length ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: T.sek }}>
                  <span>{wochentage.slice(0, 3).map((w) => `${w.tag} ${zahl(w.wert)}`).join(' · ')}</span>
                  <span>{wochentage.slice(-2).map((w) => `${w.tag} ${zahl(w.wert)}`).join(' · ')}</span>
                </div>
              ) : (
                <span className="lw-ged">{publivo?.verfuegbar ? 'Keine Werte.' : 'Nicht angebunden.'}</span>
              )}
            </div>
          </div>
          <div className="lw-gestrichelt" style={{ marginTop: 'auto', fontSize: 12, color: T.sek, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lw-weiss lw-fett">Was noch fehlt, damit die Karte voll wird</span>
            {luecken.length ? luecken.map((l, i) => <span key={i}>{l}</span>) : <span>Nichts. Alle Quellen liefern.</span>}
          </div>
        </Karte>
      </div>
    </>
  );
}
